import { Result, Group, IGroup, User } from '@/classes'
import { fetchAPI } from '@/utils/useAPI'
import { useStore } from '@/utils/useStore'
import PostsStore from '@/store/posts'
import UserStore from '@/store/user'
import ToastsStore from '@/store/toasts'
import WindowStore from '@/store/window'
import ConversationsStore from '@/store/conversations'
import { groupJoinAPI, groupKickAPI, groupRequestAnswerAPI, groupRolesSetAPI, groupRolesTransferAPI, groupsGetMineAPI, groupsGetOneAPI } from '@/api/group'

export default defineStore('groups', () => {
  const { getItem, getItems, setItem, deleteItem } = useStore('group', {
    indexes: [ '_id', 'slug' ]
  })

  const toastsStore = ToastsStore()
  const windowStore = WindowStore()
  const userStore = UserStore()
  const { currentUser } = storeToRefs(userStore)

  const join = async (
    _id: string
  ) => {
    const group = getItem(_id)

    if (group?.application === 'open') {
      await request(_id)
    } else {
      const router = useRouter()
      const route = useRoute()

      router.push({ query: { ...(route.query ?? {}), groupJoin: group?._id } })
    }

    return 
  }

  const request = async (
    _id: string,
    params?: {
      intro?: string
      token?: string
    }
  ) => {
    const response: Result<{ group: IGroup, status: string }> = await groupJoinAPI(_id, params)

    if (!response.ok) {
      toastsStore.error(response)
      return response
    }

    if (response.data.group) setItem(response.data.group, { priority: 5 })

    const route = useRoute()
    useNuxtApp().$amplitude.logEvent(`Group_Join`, {
      source: route.fullPath
    })

    await fetchMine()

    return response
  }

  const fetchMine = async () => {
    const userStore = UserStore()
    const { user } = storeToRefs(userStore)

    const response = await groupsGetMineAPI()
    
    if (response.ok) {
      response.data.groups.forEach(group => {
        setItem(group)
      })
      
      if (user.value) user.value.groups = response.data.groups.map(g => g._id)  

      return response.data
    }

    return {
      groups: [],
      unread: []
    }
  }

  const requestAnswer = async (
    groupId: string,
    userId: string,
    action: 'accept' | 'decline'
  ) => {
    const response: Result<{ group: IGroup, status: string }> = await groupRequestAnswerAPI(groupId, userId, action)

    if (!response.ok) {
      toastsStore.error(response)
      return { ok: false, error: 'error' }
    }

    if (response.data.group)  setItem(response.data.group, { priority: 5})

    return {
      ok: true,
      data: response.data
    }
  }

  const leaveAction = async (_id: string) => {
    const response: Result<{ group: Group }> = await fetchAPI('group/leave', { group: _id }, { method: 'POST' })

    if (!response.ok) {
      toastsStore.error(response)
      return { ok: false, error: 'error' }
    }

    useNuxtApp().$amplitude.logEvent(`Group_Leave`)

    if (response.data.group) setItem(response.data.group, { priority: 5 })

    return {
      ok: true,
      data: response.data.group
    }
  }

  const leave = async (_id: string) => {
    const group = getItem(_id)

    if (!group) return false

    if (group.application === 'open' && !group.isAdmin(currentUser.value?._id)) {
      return await leaveAction(_id)
    } else {
      windowStore.setAlert({
        title: useNuxtApp().$i18n.t('components.Group.leave.title'),

        content: group.isAdmin(currentUser.value?._id) ? useNuxtApp().$i18n.t('components.Group.leave.warningOrga') : useNuxtApp().$i18n.t('components.Group.leave.warning'),

        actions: [
          {
            text: useNuxtApp().$i18n.t('actions.cancel'),
            button: { color: 'weak' },
            isCloseAction: true,
            action: async () => false
          },
          {
            text: useNuxtApp().$i18n.t('components.Group.leave.action'),
            isMainAction: true,
            action: async () => {
              await leaveAction(_id)
            }
          }
        ]
      })
    }
  }

  const rolesTransfer = async (
    groupId: string
  ) => {
    const response: Result<{ group: IGroup }> = await groupRolesTransferAPI(groupId)

    if (!response.ok) {
      toastsStore.error(response)
      return { ok: false, error: 'error' }
    }

    if (response.data.group) setItem(response.data.group, { priority: 5})

    return {
      ok: true,
      data: response.data.group
    }
  }
  
  const rolesSet = async (
    userId: string, 
    role: 'admin' | 'organizer' | 'member',
    groupId: string
  ) => {
    const response: Result<{ group: IGroup }> = await groupRolesSetAPI(userId, role, groupId)

    if (!response.ok) {
      toastsStore.error(response)
      return { ok: false, error: 'error' }
    }

    if (response.data.group) setItem(response.data.group, { priority: 5})

    return {
      ok: true,
      data: response.data.group
    }
  }

  const kick = async (
    userId: string,
    groupId: string
  ) => {
    const response: Result<{ group: IGroup }> = await groupKickAPI(userId, groupId)

    if (!response.ok) {
      return false
    }

    if (response.data.group) setItem(response.data.group, { priority: 5})

    toastsStore.add({
      type: 'success',
      content: useNuxtApp().$i18n.t('components.Group.memberRemoveSuccess')
    })

    return {
      ok: true,
      data: response.data.group
    }
  }

  const fetchOne = async (params: {
    _id?: string
    slug?: string
  }) => {
    const response = await groupsGetOneAPI(params._id ?? '', {
      slug: params?.slug
    })

    if (response.ok) {
      if (response.data.group) setItem(response.data.group, { priority: 5 })

      const postsStore = PostsStore()
      const conversationStore = ConversationsStore()
      
      if (response.data.posts) response.data.posts.forEach(p => postsStore.setItem(p))
        if (response.data.conversations) response.data.conversations.map(c => conversationStore.setItem(c))
    }
    
    return response
  }

  return {
    setItem, deleteItem, rolesSet, rolesTransfer, requestAnswer,
    getItems, getItem, join, leave, kick, request, fetchOne, fetchMine
  }
})